import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, getCSSVariable, PressableBox, Text, useMedia } from '@opendoor/bricks-next';

import AccountMenu from 'components/account/AccountMenu';
import { LoginSection } from 'components/landing-pages-v2/shared/header/LoginSection';

import { useObservability } from 'helpers/observability';

import { GlobalNavSVG } from './GlobalNavSVG';

interface AccountSectionProps {
  isLoggedIn: boolean;
  showLogin: boolean;
  initials: string[];
  redirectToLogin: () => void;
  trackAccountClick: () => void;
  trackMenuClick: () => void;
  showLanguageToggle?: boolean;
  onMobileMenuToggle: () => void;
  isMobileMenuVisible: boolean;
  hamburgerRef?: React.RefObject<HTMLButtonElement>;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  isLoggedIn,
  showLogin,
  initials,
  redirectToLogin,
  trackAccountClick,
  trackMenuClick,
  showLanguageToggle,
  onMobileMenuToggle,
  isMobileMenuVisible,
  hamburgerRef,
}) => {
  const { trackJourneyEvent } = useObservability();
  const { largerThanSM } = useMedia();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, closeDropdown]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  useEffect(() => {
    if (!isExpanded) return;

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, handleEscape]);

  const handleAvatarPress = useCallback(() => {
    trackAccountClick();
    trackMenuClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackAccountClick, trackMenuClick, trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackAccountClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackAccountClick, trackJourneyEvent, redirectToLogin]);

  const handleDotsPress = useCallback(
    (e: any) => {
      e?.stopPropagation?.();
      trackMenuClick();
      toggleDropdown();
    },
    [trackMenuClick, toggleDropdown],
  );

  const handleMobileMenuPress = useCallback(() => {
    trackMenuClick();
    onMobileMenuToggle();
  }, [trackMenuClick, onMobileMenuToggle]);

  return (
    <Box ref={containerRef} position="relative" display="flex" alignItems="center" gap="$6x">
      {isLoggedIn ? (
        <>
          <PressableBox
            onPress={handleAvatarPress}
            analyticsName="account-section-click"
            aria-label="User account menu"
            aria-haspopup="true"
            aria-expanded={isExpanded}
            $smallerThanMD={{ display: 'none' }}
          >
            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              width={32}
              height={32}
              borderRadius="$60x"
              backgroundColor="$backgroundInversePrimary"
            >
              <Text
                tag="span"
                typography="$labelSmall"
                fontWeight="$medium"
                color="$contentInversePrimary"
              >
                {initials}
              </Text>
            </Box>
          </PressableBox>
          <Box $largerThanSM={{ display: 'none' }}>
            <PressableBox
              ref={hamburgerRef}
              onPress={handleMobileMenuPress}
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              style={{ color: getCSSVariable('$contentPrimary', 'color') }}
              focusStyle={{ outlineStyle: 'none' }}
              focusVisibleStyle={{ outlineStyle: 'none' }}
              aria-label="Navigation menu"
              aria-haspopup="true"
              aria-expanded={isMobileMenuVisible}
              analyticsName="cosmos-landing-page-header-menu-button"
              data-testid="icon-hamburger"
            >
              <GlobalNavSVG isOpen={isMobileMenuVisible} />
            </PressableBox>
          </Box>
        </>
      ) : (
        <Box display="flex" flexDirection="row" alignItems="center" gap="$6x">
          <LoginSection showLogin={showLogin} onPress={handleLoginPress} />
          <Box $smallerThanMD={{ display: 'none' }}>
            <PressableBox
              onPress={handleDotsPress}
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              aria-label="More options"
              aria-haspopup="true"
              aria-expanded={isExpanded}
              analyticsName="cosmos-landing-page-header-menu-button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill={getCSSVariable('$contentPrimary', 'color')}
                data-testid="icon-dots"
              >
                <circle cx="4" cy="10" r="1.75" />
                <circle cx="10" cy="10" r="1.75" />
                <circle cx="16" cy="10" r="1.75" />
              </svg>
            </PressableBox>
          </Box>
          <Box $largerThanSM={{ display: 'none' }}>
            <PressableBox
              ref={hamburgerRef}
              onPress={handleMobileMenuPress}
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              style={{ color: getCSSVariable('$contentPrimary', 'color') }}
              focusStyle={{ outlineStyle: 'none' }}
              focusVisibleStyle={{ outlineStyle: 'none' }}
              aria-label="Navigation menu"
              aria-haspopup="true"
              aria-expanded={isMobileMenuVisible}
              analyticsName="cosmos-landing-page-header-menu-button"
              data-testid="icon-hamburger"
            >
              <GlobalNavSVG isOpen={isMobileMenuVisible} />
            </PressableBox>
          </Box>
        </Box>
      )}
      {largerThanSM && (
        <AccountMenu
          isExpanded={isExpanded}
          isLoggedIn={isLoggedIn}
          showLanguageToggle={showLanguageToggle}
        />
      )}
    </Box>
  );
};
